import React, { useCallback } from "react";
import ReportingService from "Lib/API/reporting";
import { numberFormat } from "helper";
import useWidgetData from "./useWidgetData";
import WidgetWrapper from "./WidgetWrapper";
import "./widgets.scss";

const fmt = (n) => numberFormat({ number: n ?? 0, decimal: 0, style: "currency" });

// Build a smooth SVG polyline from an array of numeric values (fills full 120×36 viewBox)
const buildSparkPath = (values) => {
  if (!values || values.length < 2) return null;
  const max = Math.max(...values, 1);
  const W = 120;
  const H = 36;
  const pad = 3;
  const pts = values.map((v, i) => {
    const x = (i / (values.length - 1)) * W;
    const y = H - pad - ((v / max) * (H - pad * 2));
    return [x, y];
  });
  const line = pts.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const area = line + ` L${W},${H} L0,${H}Z`;
  return { line, area };
};

const RevenueWidget = ({ customizeMode, onRemove, dateRange, settings, onConfigure }) => {
  const filter = { ledger: settings?.ledger, subLedger: settings?.subLedger };
  const fetcher = useCallback(
    (token) => ReportingService.getWidgetRevenue(token, dateRange, filter),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dateRange, settings],
  );
  const { data, loading, error, retry } = useWidgetData(fetcher);

  const thisMonth = data?.thisMonth ?? 0;
  const lastMonth = data?.lastMonth ?? 0;
  const diff = thisMonth - lastMonth;
  const pct = lastMonth ? ((diff / lastMonth) * 100).toFixed(1) : null;
  const up = diff >= 0;

  const spark = buildSparkPath(data?.weeks);

  const title = settings?.label || "Revenue Collected";
  const hasFilter = settings?.ledgerMeta?.length > 0;

  return (
    <WidgetWrapper
      title={title}
      loading={loading}
      error={error}
      onRetry={retry}
      customizeMode={customizeMode}
      onRemove={onRemove}
      onConfigure={onConfigure}
    >
      <div className="stat-widget">
        {hasFilter && (
          <div style={{ fontSize: 11, color: "#2455ef", marginBottom: 4 }}>
            {settings.ledgerMeta.map((m) => m.label).join(", ")}
          </div>
        )}
        <div className="stat-widget__value">{fmt(thisMonth)}</div>
        <div className="stat-widget__sub">This period</div>
        {pct !== null && (
          <div className={`stat-widget__trend stat-widget__trend--${up ? "up" : "down"}`}>
            {up ? "↑" : "↓"} {Math.abs(pct)}% vs prior period
          </div>
        )}
        <div className="stat-widget__row">
          <span className="stat-widget__label">Prior period</span>
          <span className="stat-widget__val">{fmt(lastMonth)}</span>
        </div>
        {data?.ytd !== undefined && (
          <div className="stat-widget__row">
            <span className="stat-widget__label">YTD</span>
            <span className="stat-widget__val">{fmt(data.ytd)}</span>
          </div>
        )}
        {spark && (
          <div className="sparkline">
            <svg width="100%" height="36" viewBox="0 0 120 36" preserveAspectRatio="none">
              <defs>
                <linearGradient id="rev-grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#2555EF" stopOpacity="0.15" />
                  <stop offset="100%" stopColor="#2555EF" stopOpacity="0" />
                </linearGradient>
              </defs>
              <path d={spark.area} fill="url(#rev-grad)" />
              <path d={spark.line} fill="none" stroke="#2555EF" strokeWidth="1.8" />
            </svg>
          </div>
        )}
      </div>
    </WidgetWrapper>
  );
};

export default RevenueWidget;
